import { Form } from 'common-front/src/components/form/form';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { useTitle } from 'common-front/src/hooks/useTitle';
import { WorkflowInput } from 'common/src/generated/types';
import { ICreateUpdateWorkflowValues, WorkflowInputService } from 'common/src/input/workflowInput';
import { ValidateService } from 'common/src/services/validateService';
import { useService } from 'common/src/util/dependencies/dependencies';
import { WorkflowsPaths } from 'common/src/util/paths/workflowsPaths';
import * as React from 'react';
import {
    useWorkflowCreateMutation,
    useWorkflowQuery,
    useWorkflowUpdateMutation
} from '../../generated/graphqlHooks';
import { UpdateWorkflowForm } from './steps/updateWorkflowForm';

interface ICreateUpdateWorkflowProps {
    hasBeenExecuted: boolean;
    initialValues: WorkflowInput;
    isEdit: boolean;

    mutate(workflow: WorkflowInput): Promise<any>;
}

const CreateUpdateWorkflow = (props: ICreateUpdateWorkflowProps) => {
    const {
        history,
        translate,
        params: { organizationId, eventId }
    } = useHeavent();
    const stepsRef = React.useRef<HTMLDivElement | null>(null);
    const validateService = useService(ValidateService);
    const workflowInput = useService(WorkflowInputService);
    const [initialValuesString, setInitialValuesString] = React.useState('');
    const category = React.useMemo(
        () =>
            props.isEdit
                ? translate('mise_jour_de_17055', props.initialValues.name ?? '')
                : translate('cr_ation_d_une_01202'),
        [props.initialValues]
    );
    useTitle(category);

    React.useEffect(() => {
        setInitialValuesString(JSON.stringify(props.initialValues));
    }, [props.initialValues]);

    return (
        <Form
            height={1}
            hideErrors={true}
            initialValues={{
                workflow: props.initialValues
            }}
            render={({ form, handleSubmit, submitting, values }) => (
                <UpdateWorkflowForm
                    ref={stepsRef}
                    category={category}
                    change={form.change}
                    handleSubmit={handleSubmit}
                    hasBeenExecuted={props.hasBeenExecuted}
                    initialValuesString={initialValuesString}
                    isEdit={props.isEdit}
                    name={props.initialValues.name}
                    submitting={submitting}
                    workflow={values.workflow}
                />
            )}
            validate={(values: ICreateUpdateWorkflowValues) =>
                validateService.validateForForm(workflowInput.workflowInputSchema())(
                    values.workflow
                )
            }
            width={1}
            onShowErrors={() => {
                if (stepsRef.current) {
                    stepsRef.current.scrollTop = 0;
                }
            }}
            onSubmit={async (values: ICreateUpdateWorkflowValues) => {
                await props.mutate(values.workflow);

                history.push(WorkflowsPaths.WORKFLOWS({ organizationId, eventId }));
            }}
        />
    );
};

export const CreateWorkflow = () => {
    const {
        params: { eventId }
    } = useHeavent();
    const workflowInput = useService(WorkflowInputService);
    const { mutate } = useWorkflowCreateMutation();
    const initialValues = React.useMemo(() => workflowInput.workflowInputDefault(), []);

    return (
        <CreateUpdateWorkflow
            hasBeenExecuted={false}
            initialValues={initialValues}
            isEdit={false}
            mutate={(workflow) =>
                mutate({
                    eventId,
                    workflow
                })
            }
        />
    );
};

export const UpdateWorkflow = () => {
    const {
        params: { eventId, workflowId }
    } = useHeavent();
    const workflowInput = useService(WorkflowInputService);
    const { mutate } = useWorkflowUpdateMutation();
    const { data, loader } = useWorkflowQuery({ eventId, workflowId });
    const initialValues = React.useMemo(
        () => workflowInput.workflowInputDefault(data.event?.workflow),
        [data.event]
    );

    return (
        loader || (
            <CreateUpdateWorkflow
                hasBeenExecuted={data.event?.workflow.hasBeenExecuted}
                initialValues={initialValues}
                isEdit={true}
                mutate={(workflow) =>
                    mutate({
                        eventId,
                        workflowId,
                        workflow
                    })
                }
            />
        )
    );
};
